import React, { useEffect } from 'react';
import { ArrowRight, Bot, CalendarCheck, Clock, CreditCard, LayoutDashboard, MessageSquare, Shield, Smartphone, Star, Store, Truck, Users, Zap, Utensils, Briefcase } from 'lucide-react';
import { supabase } from '../supabaseClient';

export default function SaaSLandingPage() {
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) {
        window.location.href = '/v2/dashboard';
      }
    });
  }, []);

  return (
    <div className="min-h-screen bg-seryi-bg-dark text-seryi-text font-sans overflow-x-hidden">
      
      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-seryi-bg-dark/80 backdrop-blur-xl border-b border-seryi-green/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-seryi-green to-[#00cc7d] flex items-center justify-center shadow-lg shadow-seryi-green/20">
              <Bot className="text-seryi-bg-dark" size={24} />
            </div>
            <span className="text-xl font-black text-white tracking-tight">KEYLA<span className="text-seryi-green">CRM</span></span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <div className="relative group">
              <span className="text-seryi-text hover:text-white font-medium transition-colors text-sm cursor-pointer flex items-center gap-1">
                Soluciones <span className="text-[10px]">▼</span>
              </span>
              <div className="absolute top-full left-0 mt-2 w-56 bg-seryi-bg-dark border border-white/10 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all flex flex-col overflow-hidden">
                <a href="/soluciones/agenda" className="px-4 py-3 text-sm text-seryi-text hover:text-white hover:bg-white/5">Agenda Inteligente</a>
                <a href="/soluciones/inventario" className="px-4 py-3 text-sm text-seryi-text hover:text-white hover:bg-white/5">Control de Inventario</a>
                <a href="/soluciones/caja" className="px-4 py-3 text-sm text-seryi-text hover:text-white hover:bg-white/5">Sistema de Caja & Finanzas</a>
                <a href="/soluciones/wallet" className="px-4 py-3 text-sm text-seryi-text hover:text-white hover:bg-white/5">Wallet & Pagos Digitales</a>
                <a href="/soluciones/comisiones" className="px-4 py-3 text-sm text-seryi-text hover:text-white hover:bg-white/5">Comisiones a Profesionales</a>
                <a href="/soluciones/flex" className="px-4 py-3 text-sm text-seryi-text hover:text-white hover:bg-white/5">Keyla Flex & Add-ons</a>
              </div>
            </div>
            <a href="/planes" className="text-seryi-text hover:text-white font-medium transition-colors text-sm">Planes</a>
            <a href="#keyla" className="text-seryi-green font-bold transition-colors text-sm flex items-center gap-1">
              <Bot size={16} /> Keyla (IA)
            </a>
            <a href="/academy" className="text-seryi-text hover:text-white font-medium transition-colors text-sm">KeylaAcademy</a>
          </div>

          <div className="flex items-center gap-4">
            <a 
              href="/login" 
              className="hidden md:flex items-center gap-2 text-white font-medium text-sm hover:text-seryi-green transition-colors"
            >
              Iniciar Sesión
            </a>
            <a 
              href="/app/demo" 
              className="bg-seryi-green hover:bg-seryi-green/90 text-seryi-bg-dark px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-[0_0_20px_rgba(0,255,156,0.2)] hover:shadow-[0_0_30px_rgba(0,255,156,0.4)] hover:scale-105 flex items-center gap-2"
            >
              Probar Demo <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section id="sistema" className="relative pt-40 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-seryi-green/20 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute top-40 -left-20 w-[400px] h-[400px] bg-blue-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute top-60 -right-20 w-[400px] h-[400px] bg-purple-500/10 blur-[100px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,255,156,0.05)] border border-seryi-green/20 mb-8">
            <span className="w-2 h-2 rounded-full bg-seryi-green animate-pulse" />
            <span className="text-seryi-green font-medium text-sm">Keyla AI v2.0 ya está disponible</span>
          </div>
          
          <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tight mb-8 leading-[1.1]">
            El Sistema Operativo con <br className="hidden lg:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-seryi-green to-cyan-400">
              Inteligencia Artificial
            </span> para tu Negocio
          </h1>
          
          <p className="text-lg lg:text-xl text-seryi-text max-w-3xl mx-auto mb-10 leading-relaxed">
            Automatiza ventas por WhatsApp, gestiona citas, pedidos, inventario y fideliza a tus clientes. Todo en una única plataforma impulsada por agentes de IA que trabajan 24/7.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <a href="/app/demo" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-seryi-bg-light hover:bg-[#25D366]/20 hover:text-[#25D366] hover:border-[#25D366]/50 text-white border border-seryi-green/10 px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2">
              <Smartphone size={20} /> Ver Demo en Vivo
            </a>
            <a href="/signup" className="w-full sm:w-auto bg-seryi-green hover:bg-seryi-green/90 text-seryi-bg-dark px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-[0_0_30px_rgba(0,255,156,0.3)] hover:shadow-[0_0_40px_rgba(0,255,156,0.5)] hover:-translate-y-1 flex items-center justify-center gap-2">
              Empieza Ahora <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* 3 VERTICALS */}
      <section id="verticals" className="py-24 relative z-10 bg-black/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-6">Soluciones por Industria</h2>
            <p className="text-seryi-text text-lg max-w-2xl mx-auto">Configuración adaptada específicamente para el modelo de tu negocio.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-seryi-green/30 transition-colors">
              <CalendarCheck className="text-seryi-green mb-6" size={40} />
              <h3 className="text-2xl font-bold text-white mb-4">Servicios & Citas</h3>
              <p className="text-seryi-text leading-relaxed">Agendamiento automático, recordatorios por WhatsApp para evitar ausencias y módulo de comisiones para tu personal.</p>
            </div>
            
            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-orange-500/30 transition-colors">
              <Utensils className="text-orange-400 mb-6" size={40} />
              <h3 className="text-2xl font-bold text-white mb-4">Gastronomía & Comercios</h3>
              <p className="text-seryi-text leading-relaxed">Menú digital interactivo, pedidos directos por WhatsApp y panel de despacho para tus repartidores y cocina.</p>
            </div>

            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-blue-500/30 transition-colors">
              <Briefcase className="text-blue-400 mb-6" size={40} />
              <h3 className="text-2xl font-bold text-white mb-4">B2B & Servicios Profesionales</h3>
              <p className="text-seryi-text leading-relaxed">Calificación de leads con IA, Inbox unificado de conversaciones y embudos de venta para escalar tu prospección.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section id="soluciones" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-6">Beneficios y Características</h2>
            <p className="text-seryi-text text-lg max-w-2xl mx-auto">Deja de pagar múltiples suscripciones. Keyla CRM consolida todas tus operaciones comerciales.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-seryi-green/30 transition-colors group">
              <div className="w-14 h-14 bg-seryi-green/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Bot className="text-seryi-green" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Agentes IA Conversacionales</h3>
              <p className="text-seryi-text leading-relaxed">Integrados nativamente a WhatsApp para responder dudas, tomar pedidos y agendar citas 24/7 de forma autónoma.</p>
            </div>
            
            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-blue-500/30 transition-colors group">
              <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <CalendarCheck className="text-blue-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Recordatorios Automáticos</h3>
              <p className="text-seryi-text leading-relaxed">Reduce radicalmente las inasistencias de tus clientes enviando confirmaciones y recordatorios programados por WhatsApp.</p>
            </div>

            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-purple-500/30 transition-colors group">
              <div className="w-14 h-14 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="text-purple-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Paneles para Empleados</h3>
              <p className="text-seryi-text leading-relaxed">Vistas simplificadas con acceso por PIN para tu staff (/staff) donde gestionan sus citas y ven sus comisiones en tiempo real.</p>
            </div>

            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-orange-500/30 transition-colors group">
              <div className="w-14 h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Truck className="text-orange-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Portal de Repartidores</h3>
              <p className="text-seryi-text leading-relaxed">Tus repartidores o motorizados accederán a su propio panel (/delivery) para marcar los pedidos como entregados y organizar su ruta.</p>
            </div>

            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-pink-500/30 transition-colors group">
              <div className="w-14 h-14 bg-pink-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <LayoutDashboard className="text-pink-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Inbox Omnicanal CRM</h3>
              <p className="text-seryi-text leading-relaxed">Un dashboard centralizado (/login) donde monitoreas todas las conversaciones, cierras ventas complejas y administras todo el negocio.</p>
            </div>

            <div className="bg-seryi-bg-dark rounded-3xl p-8 border border-white/5 hover:border-yellow-500/30 transition-colors group">
              <div className="w-14 h-14 bg-yellow-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <CreditCard className="text-yellow-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Control de Ingresos</h3>
              <p className="text-seryi-text leading-relaxed">Analíticas de ventas, gestión de pagos, enlaces de cobro online y reportes de rendimiento de tus diferentes sedes y equipo.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CASOS DE EXITO */}
      <section id="casos" className="py-24 border-t border-white/5 bg-black/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <p className="text-sm font-medium text-seryi-text/60 mb-6">CONFIADO POR NEGOCIOS INNOVADORES DE TODOS LOS SECTORES</p>
          <div className="flex items-center justify-center gap-8 opacity-50 flex-wrap">
            <Store size={32} />
            <div className="text-2xl font-black italic tracking-tighter">SUPERMARKET</div>
            <div className="text-xl font-black">FITNESS<span className="text-seryi-green">PRO</span></div>
            <div className="text-xl font-serif">Boutique & Co.</div>
            <div className="text-xl font-black tracking-widest">CLINIC+</div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-seryi-bg-dark to-[#001a10] z-0" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="w-20 h-20 bg-seryi-green rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-[0_0_50px_rgba(0,255,156,0.3)]">
            <Zap className="text-seryi-bg-dark" size={40} />
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-8">¿Listo para revolucionar tu operación?</h2>
          <p className="text-xl text-seryi-text mb-12">
            Automatiza la interacción con clientes y obtén visibilidad 360 de todo lo que ocurre en tu empresa.
          </p>
          <a href="/signup" className="inline-flex items-center gap-3 bg-white hover:bg-gray-100 text-seryi-bg-dark px-10 py-5 rounded-2xl font-black text-xl transition-all shadow-xl hover:scale-105">
            Empieza Ahora <ArrowRight size={24} />
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-12 bg-black/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <Bot className="text-seryi-green" size={24} />
            <span className="text-xl font-black text-white tracking-tight">KEYLA<span className="text-seryi-green">CRM</span></span>
          </div>
          <p className="text-seryi-text text-sm">
            © {new Date().getFullYear()} Seryiads. Todos los derechos reservados.
          </p>
          <div className="flex gap-6 text-sm text-seryi-text">
            <a href="https://seryiads.com/privacidad" target="_blank" rel="noopener noreferrer" className="hover:text-seryi-green transition-colors">Política de Privacidad</a>
            <a href="https://seryiads.com/terminos" target="_blank" rel="noopener noreferrer" className="hover:text-seryi-green transition-colors">Términos de Servicio</a>
            <a href="https://seryiads.com" target="_blank" rel="noopener noreferrer" className="text-seryi-green font-bold hover:text-white transition-colors">Seryiads Agency</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
